import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { LogOut, Calendar, Mail, User as UserIcon, ShieldCheck } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusStyle = {
  pending: 'text-[#8B7FB8] bg-[#E5DDF0]',
  confirmed: 'text-[#2F7A4E] bg-[#DDEEE1]',
  declined: 'text-[#8A4A4A] bg-[#EED8D8]',
};

const Profile = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [b, a] = await Promise.all([
          axios.get(`${API}/bookings/me`, { withCredentials: true }),
          axios.get(`${API}/bookings/admin/is-admin`, { withCredentials: true }),
        ]);
        if (cancelled) return;
        setBookings(b.data || []);
        setIsAdmin(!!a.data?.admin);
      } catch (e) {
        // 401/403 is expected for signed-out or non-admin users; keep defaults.
        console.warn('Failed to load profile data', e);
      }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!user) return null;

  return (
    <div className="bg-[#F4EFE7] min-h-screen">
      <Navbar />

      <section className="pt-32 pb-20 max-w-[1000px] mx-auto px-8">
        <p className="text-[11px] tracking-[0.3em] text-[#8B7FB8] font-medium mb-6">ACCOUNT</p>
        <h1 className="font-serif text-5xl lg:text-6xl text-[#1E1B18] leading-[1.05] mb-10">
          Welcome back, {user.name?.split(' ')[0]}
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-14">
          <div className="lg:col-span-1 bg-[#FBF7F0] border border-[#1E1B18]/8 rounded-sm p-8">
            <div className="flex items-center gap-4">
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full object-cover" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-[#E5DDF0] flex items-center justify-center">
                  <UserIcon size={26} className="text-[#7B6BA6]" />
                </div>
              )}
              <div>
                <div className="font-medium text-[#1E1B18]">{user.name}</div>
                <div className="text-[13px] text-[#1E1B18]/60 flex items-center gap-1.5 mt-1">
                  <Mail size={13} /> {user.email}
                </div>
              </div>
            </div>

            {isAdmin && (
              <button
                onClick={() => navigate('/admin')}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#B9A9D6] hover:bg-[#A896C9] text-[#1E1B18] py-2.5 rounded-full text-[13px] font-medium transition-colors"
              >
                <ShieldCheck size={14} /> Admin dashboard
              </button>
            )}

            <button
              onClick={logout}
              className="mt-4 w-full inline-flex items-center justify-center gap-2 border border-[#1E1B18]/20 hover:border-[#1E1B18]/50 text-[#1E1B18] py-2.5 rounded-full text-[13px] font-medium transition-colors"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>

          <div className="lg:col-span-2 bg-[#FBF7F0] border border-[#1E1B18]/8 rounded-sm p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl text-[#1E1B18]">Your appointment requests</h2>
              <button
                onClick={() => { navigate('/'); setTimeout(() => document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' }), 80); }}
                className="text-[13px] text-[#8B7FB8] hover:text-[#7B6BA6] font-medium"
              >
                + New booking
              </button>
            </div>

            {loading ? (
              <div className="text-[14px] text-[#1E1B18]/60 py-10 text-center">Loading…</div>
            ) : bookings.length === 0 ? (
              <div className="text-[14px] text-[#1E1B18]/60 py-10 text-center border border-dashed border-[#1E1B18]/15 rounded-sm">
                No appointments yet. Book your first session below.
              </div>
            ) : (
              <ul className="divide-y divide-[#1E1B18]/10">
                {bookings.map((b) => (
                  <li key={b.booking_id} className="py-4 flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <div className="text-[15px] text-[#1E1B18] capitalize font-medium">
                        {b.service} · {b.duration} min
                      </div>
                      <div className="text-[13px] text-[#1E1B18]/60 flex items-center gap-1.5 mt-1">
                        <Calendar size={13} /> {b.date} at {b.time}
                      </div>
                    </div>
                    <span className={`text-[11px] tracking-[0.15em] font-medium px-3 py-1 rounded-full uppercase ${statusStyle[b.status] || statusStyle.pending}`}>
                      {b.status}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Profile;
